import { Navigate } from 'react-router-dom'
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react'
import clsx from 'clsx'
import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import PageMasthead from '../components/PageMasthead'
import { useTitle } from '../components/TitleProvider'
import PromptTestingPage from './PromptTestingPage'
import ImagePromptTestingPage from './ImagePromptTestingPage'

const LS_LAST_TAB = 'LOCAL_STORAGE_PROMPT_TESTER_TAB'

const TABS = [
    { key: 'text', label: 'Text', path: '/admin/prompt-testing/text' },
    { key: 'image', label: 'Image', path: '/admin/prompt-testing/image' }
] as const

function getTabIndex(pathname: string): number {
    if (pathname.endsWith('/image')) return 1
    return 0
}

export default function PromptTestingWrapperPage() {
    useTitle('Prompt tester')
    const location = useLocation()
    const navigate = useNavigate()
    const selectedIndex = getTabIndex(location.pathname)

    // Save the current tab to localStorage so the nav link can restore it
    useEffect(() => {
        localStorage.setItem(LS_LAST_TAB, TABS[selectedIndex].key)
    }, [selectedIndex])

    const handleTabChange = (index: number) => {
        navigate(TABS[index].path)
    }

    return (
        <div className="min-h-full bg-ink-50">
            <PageMasthead
                title="Prompt tester"
                subtitle="Test classification prompts against sample comments"
            />
            <div className="px-4 py-5 sm:px-6 lg:px-8">
                <div className="rounded-lg border border-ink-200 bg-white p-6 shadow-sm">
                    <TabGroup selectedIndex={selectedIndex} onChange={handleTabChange}>
                        <TabList className="flex border-b border-gray-200">
                            {TABS.map((tab) => (
                                <Tab
                                    key={tab.key}
                                    className={({ selected }) =>
                                        clsx(
                                            'px-4 py-2 text-sm font-medium outline-none transition-colors',
                                            selected
                                                ? 'border-b-2 border-primary-600 text-primary-600'
                                                : 'text-gray-600 hover:text-gray-900 hover:border-b-2 hover:border-gray-300'
                                        )
                                    }
                                >
                                    {tab.label}
                                </Tab>
                            ))}
                        </TabList>
                        <TabPanels>
                            <TabPanel>
                                <PromptTestingPage />
                            </TabPanel>
                            <TabPanel>
                                <ImagePromptTestingPage />
                            </TabPanel>
                        </TabPanels>
                    </TabGroup>
                </div>
            </div>
        </div>
    )
}

/** Redirects /admin/prompt-testing to the last-used tab */
export function PromptTestingRedirect() {
    const lastTab = localStorage.getItem(LS_LAST_TAB) || 'text'
    return <Navigate to={lastTab} replace />
}
